import { Link } from 'react-router-dom';
import { GUIDE, GUIDE_INTRO, type Block } from './guide/content';

// Online user guide. Same content rendered in English or Filipino based on `lang`.
export function UserGuide({ lang }: { lang: 'en' | 'fil' }) {
  const t = <T extends { en: string; fil: string }>(x: T) => x[lang];

  return (
    <div style={{ maxWidth: 880 }} className="guide-root">
      {/* Hide the app chrome + controls when printing/saving as PDF */}
      <style>{`@media print {
        .sidebar, .topbar, .guide-noprint { display: none !important; }
        .main, .content { margin: 0 !important; padding: 0 !important; }
        .guide-root { max-width: 100% !important; }
        .card { box-shadow: none !important; border: 1px solid #ccc; break-inside: avoid; }
        a { color: #000 !important; text-decoration: none !important; }
        body { background: #fff !important; }
      }`}</style>

      {/* Language switch + Print (hidden on the printed page) */}
      <div className="guide-noprint" style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center' }}>
        <Link className={`btn btn-sm ${lang === 'en' ? 'btn-primary' : 'btn-ghost'}`} to="/guide">English</Link>
        <Link className={`btn btn-sm ${lang === 'fil' ? 'btn-primary' : 'btn-ghost'}`} to="/guide/fil">Filipino</Link>
        <button className="btn btn-sm btn-gold" style={{ marginLeft: 'auto' }} onClick={() => window.print()}>
          🖨 {lang === 'en' ? 'Print / Save as PDF' : 'I-print / I-save bilang PDF'}
        </button>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 8 }}>
        <img src="/logo-white.png" alt="The AI Solution for 3D" style={{ width: 64, height: 64, objectFit: 'contain' }} />
        <div>
          <h1 style={{ margin: 0 }}>{t(GUIDE_INTRO.title)}</h1>
          <p className="muted" style={{ margin: '4px 0 0' }}>{t(GUIDE_INTRO.subtitle)}</p>
        </div>
      </div>

      {/* Table of contents */}
      <div className="card" style={{ marginBottom: 16 }}>
        <strong>{lang === 'en' ? 'On this page' : 'Nasa pahinang ito'}:</strong>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
          {GUIDE.map((s) => (
            <a key={s.id} href={`#${s.id}`} className="btn btn-ghost btn-sm" style={{ textDecoration: 'none' }}>
              {s.emoji} {t(s.title)}
            </a>
          ))}
        </div>
      </div>

      {GUIDE.map((s) => (
        <section key={s.id} id={s.id} className="card" style={{ marginBottom: 14, scrollMarginTop: 16 }}>
          <h2 style={{ marginTop: 0 }}>{s.emoji} {t(s.title)}</h2>
          {s.blocks.map((b, i) => (
            <GuideBlock key={i} block={b} text={t(b)} num={stepNumber(s.blocks, i)} />
          ))}
        </section>
      ))}

      <p className="muted" style={{ fontSize: 13 }}>
        {lang === 'en'
          ? 'Need help the guide does not cover? Ask your admin or estimator lead.'
          : 'May tanong na wala dito sa gabay? Tanungin ang iyong admin o estimator lead.'}
      </p>
    </div>
  );
}

// Number steps within a section (1, 2, 3…) so walkthroughs read as a numbered list.
function stepNumber(blocks: Block[], i: number): number | null {
  if (blocks[i].kind !== 'step') return null;
  let n = 0;
  for (let k = 0; k <= i; k++) if (blocks[k].kind === 'step') n++;
  return n;
}

function GuideBlock({ block, text, num }: { block: Block; text: string; num: number | null }) {
  if (block.kind === 'tip') {
    return (
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', background: 'var(--gold-light)', border: '1px solid var(--gold)', borderRadius: 8, padding: '10px 12px', margin: '10px 0' }}>
        <span style={{ fontSize: 18 }}>💡</span>
        <p style={{ margin: 0, fontSize: 14 }}>{text}</p>
      </div>
    );
  }
  if (block.kind === 'step') {
    return (
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', margin: '8px 0' }}>
        <span style={{ flex: '0 0 auto', width: 24, height: 24, borderRadius: '50%', background: 'var(--teal)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700 }}>{num}</span>
        <p style={{ margin: 0, lineHeight: 1.5 }}>{text}</p>
      </div>
    );
  }
  return <p style={{ lineHeight: 1.6 }}>{text}</p>;
}
